import { useState, type FormEvent } from "react";
import {
  addPipelineStage,
  removePipelineStage,
  reorderPipelineStages,
  updatePipeline,
  type Pipeline,
} from "@/hooks/use-deals";
import { ArrowDown, ArrowUp, Plus, Trash2, X } from "lucide-react";

interface PipelineConfigPanelProps {
  pipeline: Pipeline;
  onClose: () => void;
  onChanged: () => Promise<void>;
}

const DEFAULT_STAGE_COLOR = "#3B82F6";

export function PipelineConfigPanel({ pipeline, onClose, onChanged }: PipelineConfigPanelProps) {
  const [name, setName] = useState(pipeline.name);
  const [isDefault, setIsDefault] = useState(Boolean(pipeline.isDefault));
  const [stageName, setStageName] = useState("");
  const [stageColor, setStageColor] = useState(DEFAULT_STAGE_COLOR);
  const [terminalType, setTerminalType] = useState<"open" | "won" | "lost">("open");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runAction = async (action: () => Promise<void>) => {
    setIsSaving(true);
    setError(null);
    try {
      await action();
      await onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Pipeline update failed");
    } finally {
      setIsSaving(false);
    }
  };

  const saveMetadata = (event: FormEvent) => {
    event.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) return;
    void runAction(() => updatePipeline(pipeline.id, { name: trimmedName, isDefault }));
  };

  const addStage = (event: FormEvent) => {
    event.preventDefault();
    const trimmedName = stageName.trim();
    if (!trimmedName) return;

    void runAction(async () => {
      await addPipelineStage(pipeline.id, {
        id: uniqueStageId(trimmedName, pipeline.stages.map((stage) => stage.id)),
        name: trimmedName,
        color: stageColor,
        isWinStage: terminalType === "won",
        isLossStage: terminalType === "lost",
        maxDays: null,
        requiredFields: [],
        autoTasks: [],
      });
      setStageName("");
      setTerminalType("open");
    });
  };

  const moveStage = (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= pipeline.stages.length) return;
    const stageIds = pipeline.stages.map((stage) => stage.id);
    const [stageId] = stageIds.splice(index, 1);
    if (!stageId) return;
    stageIds.splice(nextIndex, 0, stageId);
    void runAction(() => reorderPipelineStages(pipeline.id, stageIds));
  };

  const removeStage = (stageId: string) => {
    const confirmed = typeof window === "undefined" || window.confirm("Remove this stage?");
    if (!confirmed) return;
    void runAction(() => removePipelineStage(pipeline.id, stageId));
  };

  return (
    <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4 shadow-sm">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-primary)]">Pipeline configuration</p>
          <h2 className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{pipeline.name}</h2>
          <p className="text-sm text-[var(--color-muted-foreground)]">Edit metadata, add stages, remove empty stages, or reorder the board.</p>
        </div>
        <button type="button" onClick={onClose} className="rounded-md border border-[var(--color-border)] p-2 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
          <X className="h-4 w-4" />
        </button>
      </div>

      {error && (
        <p className="mb-3 rounded-md border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-3 py-2 text-sm text-[var(--color-destructive)]">
          {error}
        </p>
      )}

      <form onSubmit={saveMetadata} className="grid gap-3 rounded-xl border border-[var(--color-border)] p-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
        <label className="grid gap-1 text-xs font-medium text-[var(--color-muted-foreground)]">
          Pipeline name
          <input value={name} onChange={(event) => setName(event.target.value)} className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]" />
        </label>
        <label className="flex items-center gap-2 rounded-md border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-foreground)]">
          <input type="checkbox" checked={isDefault} onChange={(event) => setIsDefault(event.target.checked)} />
          Default
        </label>
        <button type="submit" disabled={isSaving || !name.trim()} className="rounded-md bg-[var(--color-primary)] px-3 py-2 text-sm font-medium text-white disabled:opacity-50">
          Save
        </button>
      </form>

      <div className="mt-4 space-y-2">
        {pipeline.stages.map((stage, index) => (
          <div key={stage.id} className="flex items-center gap-2 rounded-xl border border-[var(--color-border)] p-3">
            <span className="h-3 w-3 rounded-full" style={{ backgroundColor: stage.color ?? DEFAULT_STAGE_COLOR }} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-[var(--color-foreground)]">{stage.name}</p>
              <p className="text-xs text-[var(--color-muted-foreground)]">
                {stage.isWinStage ? "Won stage" : stage.isLossStage ? "Lost stage" : "Open stage"}
              </p>
            </div>
            <button type="button" disabled={index === 0 || isSaving} onClick={() => moveStage(index, -1)} className="rounded-md border border-[var(--color-border)] p-1.5 disabled:opacity-40">
              <ArrowUp className="h-4 w-4" />
            </button>
            <button type="button" disabled={index === pipeline.stages.length - 1 || isSaving} onClick={() => moveStage(index, 1)} className="rounded-md border border-[var(--color-border)] p-1.5 disabled:opacity-40">
              <ArrowDown className="h-4 w-4" />
            </button>
            <button type="button" disabled={pipeline.stages.length <= 2 || isSaving} onClick={() => removeStage(stage.id)} className="rounded-md border border-[var(--color-destructive)]/30 p-1.5 text-[var(--color-destructive)] disabled:opacity-40">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      <form onSubmit={addStage} className="mt-4 grid gap-3 rounded-xl border border-dashed border-[var(--color-border)] p-3 sm:grid-cols-[1fr_auto_auto_auto] sm:items-end">
        <label className="grid gap-1 text-xs font-medium text-[var(--color-muted-foreground)]">
          New stage
          <input value={stageName} onChange={(event) => setStageName(event.target.value)} placeholder="Demo booked" className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]" />
        </label>
        <label className="grid gap-1 text-xs font-medium text-[var(--color-muted-foreground)]">
          Color
          <input type="color" value={stageColor} onChange={(event) => setStageColor(event.target.value)} className="h-10 rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-2" />
        </label>
        <label className="grid gap-1 text-xs font-medium text-[var(--color-muted-foreground)]">
          Type
          <select value={terminalType} onChange={(event) => setTerminalType(event.target.value as "open" | "won" | "lost")} className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]">
            <option value="open">Open</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
          </select>
        </label>
        <button type="submit" disabled={isSaving || !stageName.trim()} className="inline-flex items-center justify-center gap-1 rounded-md border border-[var(--color-primary)] px-3 py-2 text-sm font-medium text-[var(--color-primary)] disabled:opacity-50">
          <Plus className="h-4 w-4" />
          Add
        </button>
      </form>
    </section>
  );
}

function uniqueStageId(name: string, existingIds: string[]): string {
  const base = name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "stage";
  let candidate = base;
  let suffix = 2;
  while (existingIds.includes(candidate)) {
    candidate = `${base}_${suffix}`;
    suffix += 1;
  }
  return candidate;
}
